import { createStreamProcessingState, extractRemainingFields, finalParse, handleStreamChunk, } from "./StreamChunkProcessor";
const ARG_LOG_PREVIEW_LEN = 160;
const ARG_LOG_TAIL_LEN = 240;
const ARG_LOG_MIN_INTERVAL_MS = 120;
const ARG_LOG_MIN_LENGTH_DELTA = 96;
function compactForLog(value, maxLen) {
    const compact = value.replace(/\s+/g, " ").trim();
    if (compact.length <= maxLen) {
        return compact;
    }
    return `...${compact.slice(-maxLen)}`;
}
function hasStreamingConfig(fieldsToCheck, listsToCheck) {
    return (fieldsToCheck?.length || 0) > 0 || (listsToCheck?.length || 0) > 0;
}
function createExtractOnlyOperation(toolName, fieldsToCheck, listsToCheck) {
    const extractedData = {};
    return {
        name: toolName,
        fieldsToCheck,
        listsToCheck,
        addStep(fieldName, value) {
            if (listsToCheck.some((field) => field.name === fieldName)) {
                const existing = extractedData[fieldName];
                const list = Array.isArray(existing) ? existing : [];
                list.push(value);
                extractedData[fieldName] = list;
                return;
            }
            extractedData[fieldName] = value;
        },
    };
}
export async function consumeStreamingToolCalls(chunks, options) {
    const callbacks = options.callbacks;
    let assistantText = "";
    let tokensUsed = 0;
    const callMap = new Map();
    const toolStartFired = new Set();
    const streamStates = new Map();
    const streamOperations = new Map();
    const streamedCallIndices = new Set();
    const streamStartAt = Date.now();
    const argLogState = new Map();
    for await (const chunk of chunks) {
        const choice = chunk.choices?.[0];
        const delta = choice?.delta;
        if (typeof chunk.usage?.total_tokens === "number" && chunk.usage.total_tokens > 0) {
            tokensUsed = chunk.usage.total_tokens;
        }
        if (typeof delta?.content === "string" && delta.content.length > 0) {
            assistantText += delta.content;
            callbacks?.onTextDelta?.(delta.content);
        }
        if (!Array.isArray(delta?.tool_calls)) {
            continue;
        }
        for (const incoming of delta.tool_calls) {
            const index = typeof incoming.index === "number" ? incoming.index : 0;
            const existing = callMap.get(index) || {
                index,
                id: "",
                name: "",
                arguments: "",
            };
            if (typeof incoming.id === "string" && incoming.id) {
                existing.id = incoming.id;
            }
            if (typeof incoming.function?.name === "string" && incoming.function.name) {
                existing.name = incoming.function.name;
            }
            const argsDelta = incoming.function?.arguments;
            if (typeof argsDelta === "string" && argsDelta.length > 0) {
                existing.arguments += argsDelta;
                const now = Date.now();
                const last = argLogState.get(index);
                const totalLen = existing.arguments.length;
                const shouldLog = !last ||
                    now - last.atMs >= ARG_LOG_MIN_INTERVAL_MS ||
                    totalLen - last.totalLen >= ARG_LOG_MIN_LENGTH_DELTA;
                if (shouldLog) {
                    const elapsedMs = now - streamStartAt;
                    console.log(`[llm-streaming][tool-args][${elapsedMs}ms] index=${index} tool=${existing.name || "<pending>"} deltaLen=${argsDelta.length} totalLen=${totalLen} delta="${compactForLog(argsDelta, ARG_LOG_PREVIEW_LEN)}" tail="${compactForLog(existing.arguments, ARG_LOG_TAIL_LEN)}"`);
                    argLogState.set(index, { atMs: now, totalLen });
                }
            }
            callMap.set(index, existing);
            if (existing.name && !toolStartFired.has(index)) {
                const tool = options.resolveTool(existing.name);
                const candidateOperation = tool?.streamableOperation;
                const fieldsToCheck = tool?.fieldsToCheck ?? candidateOperation?.fieldsToCheck ?? [];
                const listsToCheck = tool?.listsToCheck ?? candidateOperation?.listsToCheck ?? [];
                const hasStreaming = hasStreamingConfig(fieldsToCheck, listsToCheck);
                callbacks?.onToolStart?.(existing.name, hasStreaming, tool?.isAgentTool, index);
                toolStartFired.add(index);
                if (hasStreaming) {
                    const operation = candidateOperation ?? createExtractOnlyOperation(existing.name, fieldsToCheck, listsToCheck);
                    const maybeReset = operation.reset;
                    if (typeof maybeReset === "function") {
                        maybeReset.call(operation);
                    }
                    streamOperations.set(index, operation);
                    streamStates.set(index, createStreamProcessingState());
                    streamedCallIndices.add(index);
                }
            }
            if (existing.name &&
                typeof argsDelta === "string" &&
                argsDelta.length > 0) {
                callbacks?.onToolCallDelta?.({
                    index,
                    id: existing.id,
                    toolName: existing.name,
                    argsDelta,
                    accumulatedArgs: existing.arguments,
                });
                const operation = streamOperations.get(index);
                const state = streamStates.get(index);
                if (operation && state) {
                    const operationType = options.getOperationType?.(existing.name) ?? "default";
                    handleStreamChunk(operation, argsDelta, state, operationType, {
                        onFieldParsed: (fieldName, value) => {
                            callbacks?.onOperationField?.(existing.name, fieldName, value, index);
                        },
                        onListItemParsed: (fieldName, itemIndex, value) => {
                            callbacks?.onOperationField?.(existing.name, `${fieldName}[${itemIndex}]`, value, index);
                        },
                        onParseError: (error, currentJson) => {
                            callbacks?.onParseError?.({
                                toolName: existing.name,
                                callIndex: index,
                                error,
                                currentJson,
                            });
                        },
                    });
                }
            }
        }
    }
    // Extract any remaining fields/items that never stabilized during streaming.
    for (const [index, operation] of streamOperations.entries()) {
        const state = streamStates.get(index);
        if (!state)
            continue;
        const parsed = finalParse(state);
        if (!parsed)
            continue;
        extractRemainingFields(operation, parsed, state, {
            onFieldParsed: (fieldName, value) => {
                callbacks?.onOperationField?.(operation.name, fieldName, value, index);
            },
            onListItemParsed: (fieldName, itemIndex, value) => {
                callbacks?.onOperationField?.(operation.name, `${fieldName}[${itemIndex}]`, value, index);
            },
        });
    }
    const toolCalls = Array.from(callMap.values())
        .sort((a, b) => a.index - b.index)
        .filter((call) => typeof call.name === "string" && call.name.length > 0);
    for (const call of toolCalls) {
        console.log(`[llm-streaming][tool-args][done] index=${call.index} tool=${call.name} finalLen=${call.arguments.length}`);
    }
    return {
        assistantText,
        toolCalls,
        tokensUsed,
        streamedCallIndices,
    };
}
